import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, XCircle, RefreshCcw, Package, ShoppingCart,
  DollarSign, Loader2, ExternalLink, AlertCircle, Clock, Store,
  Calculator, LinkIcon, Unplug, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { api, API_BASE } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { formatDate } from '../lib/utils';
// Fix 51 (2026-10-06): los contactos de iCloud pasaron a su propia pantalla
// (Agenda iCloud); acá queda solo un acceso directo.
import { Link } from 'react-router-dom';

const TIKTOK_PARTNER_URL = 'https://partner.us.tiktokshop.com/';

function formatExpiry(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = d.getTime() - now;
    const hours = Math.round(diff / (1000 * 60 * 60));
    if (Math.abs(hours) > 24 * 30) {
      const days = Math.round(diff / (1000 * 60 * 60 * 24));
      return `${d.toLocaleDateString()} (${days > 0 ? '+' : ''}${days}d)`;
    }
    return `${d.toLocaleString()} (${hours > 0 ? '+' : ''}${hours}h)`;
  } catch {
    return iso;
  }
}

function StatusPill({ ok, label }) {
  const color = ok ? '#34D399' : '#F87171';
  const bg = ok ? 'rgba(52,211,153,0.08)' : 'rgba(248,113,113,0.08)';
  const border = ok ? 'rgba(52,211,153,0.18)' : 'rgba(248,113,113,0.18)';
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{
        padding: '4px 10px',
        background: bg,
        border: `1px solid ${border}`,
        color,
      }}
      data-testid={`status-pill-${ok ? 'ok' : 'fail'}`}
    >
      <span
        className="h-[5px] w-[5px] rounded-full"
        style={{
          background: color,
          ...(ok ? { animation: 'pulseDot 2.4s ease infinite' } : {}),
        }}
      />
      {label}
    </span>
  );
}

export default function IntegrationsPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  const connectionQ = useQuery({
    queryKey: ['integrations', 'tiktok', 'connection'],
    queryFn: async () => (await api.get('/tiktok/connection')).data,
    refetchInterval: 30000,
  });

  const logsQ = useQuery({
    queryKey: ['integrations', 'tiktok', 'logs'],
    queryFn: async () => (await api.get('/tiktok/sync/logs?limit=20')).data,
    refetchInterval: 15000,
  });

  const refreshM = useMutation({
    mutationFn: async () => (await api.post('/tiktok/connection/refresh')).data,
    onSuccess: () => {
      toast.success(t('integrations.toast_refresh_ok'));
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err) => toast.error(
      `${t('integrations.toast_refresh_err')}: ${err?.response?.data?.detail || err.message}`
    ),
  });

  const shopsM = useMutation({
    mutationFn: async () => (await api.get('/tiktok/shops')).data,
    onSuccess: (d) => {
      toast.success(`${t('integrations.toast_shops_ok')} (${d.count})`);
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err) => toast.error(
      `${t('integrations.toast_shops_err')}: ${err?.response?.data?.detail || err.message}`
    ),
  });

  const productsM = useMutation({
    mutationFn: async () => (await api.post('/tiktok/sync/products?max_pages=20&page_size=50')).data,
    onSuccess: (d) => {
      toast.success(
        `${t('integrations.toast_products_ok')}: ${d.pulled} · ${d.new_mappings} ${t('integrations.new')}`
      );
      qc.invalidateQueries({ queryKey: ['integrations'] });
      qc.invalidateQueries({ queryKey: ['config', 'products'] });
    },
    onError: (err) => toast.error(
      `${t('integrations.toast_products_err')}: ${err?.response?.data?.detail || err.message}`
    ),
  });

  const ordersM = useMutation({
    mutationFn: async () => (await api.post('/tiktok/sync/orders?hours_back=24&max_pages=20')).data,
    onSuccess: (d) => {
      toast.success(
        `${t('integrations.toast_orders_ok')}: ${d.pulled} · ${d.processed} ${t('integrations.processed')}`
      );
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err) => toast.error(
      `${t('integrations.toast_orders_err')}: ${err?.response?.data?.detail || err.message}`
    ),
  });

  const settlementsM = useMutation({
    mutationFn: async () => (await api.post('/tiktok/sync/settlements?days_back=30')).data,
    onSuccess: (d) => {
      toast.success(
        `${t('integrations.toast_settlements_ok')}: ${d.pulled} · ${d.new} ${t('integrations.new')}`
      );
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err) => toast.error(
      `${t('integrations.toast_settlements_err')}: ${err?.response?.data?.detail || err.message}`
    ),
  });

  const conn = connectionQ.data || {};
  const livePing = conn.live_ping || {};
  const isConnected = Boolean(conn.connected);
  const livePingOk = Boolean(livePing.ok);

  const authorizeUrl = `${API_BASE}/auth/tiktok/authorize`;

  return (
    <div data-testid="integrations-page" className="space-y-6">
      <PageHeader
        title={t('integrations.title')}
        subtitle={t('integrations.subtitle')}
      />

      {/* TikTok Shop card */}
      <Card className="overflow-hidden" data-testid="tiktok-card">
        <CardHeader className="pb-4 pt-6 px-7">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3.5">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-xl shrink-0"
                style={{
                  background: 'var(--surface-2, hsl(var(--surface-2)))',
                  border: '1px solid var(--border-default)',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <Store className="h-[22px] w-[22px]" style={{ color: '#D4A843' }} strokeWidth={1.6} />
              </div>
              <div>
                <CardTitle className="text-[16px] font-bold tracking-tight text-foreground">TikTok Shop</CardTitle>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  {t('integrations.tiktok_subtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill
                ok={isConnected}
                label={isConnected ? t('integrations.connected') : t('integrations.not_connected')}
              />
              {isConnected && (
                <StatusPill
                  ok={livePingOk}
                  label={livePingOk ? t('integrations.live_ok') : t('integrations.live_fail')}
                />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {connectionQ.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !isConnected ? (
            <div className="space-y-3">
              <EmptyState
                message={t('integrations.empty_not_connected')}
                testid="tiktok-not-connected"
              />
              <div className="flex justify-center">
                <a href={authorizeUrl} target="_blank" rel="noreferrer">
                  <Button data-testid="tiktok-connect-button">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t('integrations.connect_tiktok')}
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <>
              {/* Connection details grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailRow label={t('integrations.seller_name')} value={conn.seller_name} testid="tk-seller" />
                <DetailRow label={t('integrations.seller_region')} value={conn.seller_base_region} testid="tk-region" />
                <DetailRow
                  label={t('integrations.shop_id')}
                  value={conn.shop_id || '—'}
                  mono
                  testid="tk-shop"
                />
                <DetailRow
                  label={t('integrations.shop_cipher')}
                  value={conn.shop_cipher_present ? '✓' : t('integrations.missing')}
                  testid="tk-cipher"
                />
                <DetailRow
                  label={t('integrations.access_expires')}
                  value={formatExpiry(conn.access_expires_at)}
                  warn={!conn.access_expires_at}
                  testid="tk-access-exp"
                />
                <DetailRow
                  label={t('integrations.refresh_expires')}
                  value={formatExpiry(conn.refresh_expires_at)}
                  testid="tk-refresh-exp"
                />
                <DetailRow
                  label={t('integrations.authorized_at')}
                  value={conn.authorized_at ? new Date(conn.authorized_at).toLocaleString() : '—'}
                  testid="tk-authorized"
                />
                <DetailRow
                  label={t('integrations.refreshed_at')}
                  value={conn.refreshed_at ? new Date(conn.refreshed_at).toLocaleString() : t('integrations.never')}
                  testid="tk-refreshed"
                />
              </div>

              {/* Scopes */}
              {conn.scopes && conn.scopes.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-muted-foreground mb-2">
                    {t('integrations.scopes')} ({conn.scopes.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {conn.scopes.map((s) => (
                      <Badge
                        key={s}
                        variant="secondary"
                        className="font-mono text-[10px] py-0.5"
                        data-testid={`scope-${s}`}
                      >
                        {s}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Live ping result */}
              {livePing && (
                <div
                  className={`rounded-lg p-3 text-xs ${
                    livePingOk
                      ? 'bg-emerald-500/5 ring-1 ring-emerald-500/20'
                      : 'bg-rose-500/5 ring-1 ring-rose-500/20'
                  }`}
                  data-testid="tk-live-ping"
                >
                  {livePingOk ? (
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>
                        {t('integrations.live_ping_ok')}: {livePing.shops_count}{' '}
                        {t('integrations.shops')}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        {t('integrations.live_ping_fail')}: {livePing.message || livePing.error || `code ${livePing.code}`}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <Separator />

              {/* Action buttons */}
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-2">
                  {t('integrations.actions')}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refreshM.mutate()}
                    disabled={refreshM.isPending}
                    data-testid="tk-refresh-tokens"
                  >
                    {refreshM.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCcw className="h-4 w-4 mr-2" />
                    )}
                    {t('integrations.refresh_tokens')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => shopsM.mutate()}
                    disabled={shopsM.isPending}
                    data-testid="tk-sync-shops"
                  >
                    {shopsM.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Store className="h-4 w-4 mr-2" />
                    )}
                    {t('integrations.discover_shops')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => productsM.mutate()}
                    disabled={productsM.isPending || !conn.shop_cipher_present}
                    data-testid="tk-sync-products"
                  >
                    {productsM.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Package className="h-4 w-4 mr-2" />
                    )}
                    {t('integrations.sync_products')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setSyncDialogOpen(true)}
                    disabled={ordersM.isPending || !conn.shop_cipher_present || !isAdmin}
                    title={!isAdmin ? 'Solo un administrador puede sincronizar manualmente' : undefined}
                    data-testid="tk-sync-orders"
                  >
                    {ordersM.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShoppingCart className="h-4 w-4 mr-2" />
                    )}
                    {t('integrations.sync_orders')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => settlementsM.mutate()}
                    disabled={settlementsM.isPending || !conn.shop_cipher_present}
                    data-testid="tk-sync-settlements"
                    className="col-span-2 sm:col-span-1"
                  >
                    {settlementsM.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <DollarSign className="h-4 w-4 mr-2" />
                    )}
                    {t('integrations.sync_settlements')}
                  </Button>
                  <a
                    href={TIKTOK_PARTNER_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="col-span-2 sm:col-span-3 inline-flex"
                  >
                    <Button variant="ghost" size="sm" className="w-full" data-testid="tk-partner-center">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t('integrations.open_partner_center')}
                    </Button>
                  </a>
                </div>
                {!conn.shop_cipher_present && (
                  <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {t('integrations.cipher_hint')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Manual order-sync confirmation dialog (admin only) */}
      <AlertDialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <AlertDialogContent data-testid="sync-orders-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('search.sync_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('search.sync_confirm_body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sync-orders-confirm-cancel">
              {t('common.clear') === 'Limpiar' ? 'Cancelar' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="sync-orders-confirm-accept"
              onClick={(e) => { e.preventDefault(); setSyncDialogOpen(false); ordersM.mutate(); }}
            >
              {t('search.sync_confirm_accept')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QuickBooks Online card */}
      <QBOCard />

      {/* Recent sync logs */}
      <Card data-testid="tiktok-logs-card">
        <CardHeader className="pb-3 pt-6 px-7">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-semibold">
                {t('integrations.recent_logs')}
              </CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: ['integrations', 'tiktok', 'logs'] })}
              data-testid="tk-logs-refresh"
            >
              <RefreshCcw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {logsQ.isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (logsQ.data?.items || []).length === 0 ? (
            <EmptyState message={t('integrations.no_logs')} testid="tk-logs-empty" />
          ) : (
            <Table data-testid="tk-logs-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">{t('common.date')}</TableHead>
                  <TableHead className="w-[110px]">{t('integrations.source')}</TableHead>
                  <TableHead className="w-[80px]">{t('integrations.level')}</TableHead>
                  <TableHead>{t('integrations.message')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(logsQ.data?.items || []).map((log, idx) => (
                  <TableRow key={`${log.created_at}-${idx}`}>
                    <TableCell className="text-xs font-mono text-muted-foreground">
                      {formatDate(log.created_at)}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {log.source?.replace('tiktok.', '') || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={log.level === 'error' ? 'destructive' : 'secondary'}
                        className="text-[10px] uppercase"
                      >
                        {log.level || 'info'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{log.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Fix 51 (2026-10-06): contactos de iCloud — acceso a su pantalla (solo admin). */}
      {isAdmin && (
        <Card data-testid="icloud-agenda-link-card">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-[14px] font-medium">{t('agenda_link.title')}</div>
                <div className="text-[12px] text-muted-foreground">{t('agenda_link.text')}</div>
              </div>
            </div>
            <Button asChild variant="secondary" data-testid="icloud-agenda-link">
              <Link to="/agenda">{t('agenda_link.button')}</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono, warn, testid }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={`text-sm ${mono ? 'font-mono' : ''} ${warn ? 'text-amber-600 dark:text-amber-400' : ''}`}
      >
        {value || '—'}
      </span>
    </div>
  );
}

function QBOCard() {
  const { t } = useLang();
  const qc = useQueryClient();

  const connQ = useQuery({
    queryKey: ['integrations', 'qbo', 'connection'],
    queryFn: async () => (await api.get('/qbo/connection')).data,
    refetchInterval: 30000,
  });

  const refreshM = useMutation({
    mutationFn: async () => (await api.post('/qbo/refresh')).data,
    onSuccess: () => {
      toast.success(t('integrations.toast_refresh_ok'));
      qc.invalidateQueries({ queryKey: ['integrations', 'qbo'] });
    },
    onError: (err) => toast.error(
      `${t('integrations.toast_refresh_err')}: ${err?.response?.data?.detail || err.message}`
    ),
  });

  const bootstrapM = useMutation({
    mutationFn: async () => (await api.post('/qbo/bootstrap-accounts')).data,
    onSuccess: (d) => {
      toast.success(
        t('integrations.qbo_bootstrap_done')
          .replace('{{created}}', d.created?.length ?? 0)
          .replace('{{skipped}}', d.skipped?.length ?? 0)
      );
      qc.invalidateQueries({ queryKey: ['integrations', 'qbo'] });
    },
    onError: (err) => toast.error(
      `${t('integrations.qbo_bootstrap_err')}: ${err?.response?.data?.detail || err.message}`
    ),
  });

  const disconnectM = useMutation({
    mutationFn: async () => (await api.delete('/qbo/disconnect')).data,
    onSuccess: () => {
      toast.success(t('integrations.qbo_disconnected'));
      qc.invalidateQueries({ queryKey: ['integrations', 'qbo'] });
    },
  });

  const data = connQ.data || {};
  const isConnected = Boolean(data.connected);
  const appConfigured = Boolean(data.app_configured);
  const livePing = data.live_ping || {};
  const livePingOk = Boolean(livePing.ok);
  const authorizeUrl = `${API_BASE}/auth/qbo/authorize`;

  return (
    <Card data-testid="qbo-card">
      <CardHeader className="pb-4 pt-6 px-7">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3.5">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl shrink-0"
              style={{
                background: 'hsl(var(--surface-2))',
                border: '1px solid var(--border-default)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <Calculator className="h-[22px] w-[22px]" style={{ color: 'var(--success-fg)' }} strokeWidth={1.6} />
            </div>
            <div>
              <CardTitle className="text-[16px] font-bold tracking-tight text-foreground">
                {t('integrations.qbo_card_title')}
              </CardTitle>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                {t('integrations.qbo_subtitle')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill
              ok={isConnected}
              label={isConnected ? t('integrations.connected') : t('integrations.not_connected')}
            />
            {isConnected && (
              <StatusPill
                ok={livePingOk}
                label={livePingOk ? t('integrations.live_ok') : t('integrations.live_fail')}
              />
            )}
            {data.environment && (
              <Badge
                variant="outline"
                className="font-mono text-[10px] uppercase"
                data-testid="qbo-env-badge"
              >
                {data.environment}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {connQ.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !appConfigured ? (
          <div className="rounded-lg bg-amber-500/5 ring-1 ring-amber-500/20 p-4 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2" data-testid="qbo-app-missing">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{t('integrations.qbo_app_missing')}</span>
          </div>
        ) : !isConnected ? (
          <div className="space-y-3">
            <EmptyState
              message={t('integrations.qbo_not_connected')}
              testid="qbo-not-connected"
            />
            <div className="flex justify-center">
              <a href={authorizeUrl} target="_blank" rel="noreferrer">
                <Button data-testid="qbo-connect-button">
                  <LinkIcon className="h-4 w-4 mr-2" />
                  {t('integrations.qbo_connect')}
                </Button>
              </a>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailRow
                label={t('integrations.qbo_company')}
                value={livePing.company_name}
                testid="qbo-company"
              />
              <DetailRow
                label={t('integrations.qbo_environment')}
                value={data.environment}
                mono
                testid="qbo-env"
              />
              <DetailRow
                label={t('integrations.qbo_realm')}
                value={data.realm_id}
                mono
                testid="qbo-realm"
              />
              <DetailRow
                label={t('integrations.qbo_managed_accounts')}
                value={String(data.managed_accounts_count ?? 0)}
                testid="qbo-managed"
              />
              <DetailRow
                label={t('integrations.access_expires')}
                value={formatExpiry(data.access_expires_at)}
                warn={!data.access_expires_at}
                testid="qbo-access-exp"
              />
              <DetailRow
                label={t('integrations.refresh_expires')}
                value={formatExpiry(data.refresh_expires_at)}
                testid="qbo-refresh-exp"
              />
              <DetailRow
                label={t('integrations.authorized_at')}
                value={data.authorized_at ? new Date(data.authorized_at).toLocaleString() : '—'}
                testid="qbo-auth-at"
              />
              <DetailRow
                label={t('integrations.refreshed_at')}
                value={data.refreshed_at ? new Date(data.refreshed_at).toLocaleString() : t('integrations.never')}
                testid="qbo-refreshed"
              />
            </div>

            {livePing && livePing.ok === false && (
              <div className="rounded-lg p-3 text-xs bg-rose-500/5 ring-1 ring-rose-500/20 text-rose-700 dark:text-rose-400 flex items-center gap-2" data-testid="qbo-live-ping-fail">
                <AlertCircle className="h-4 w-4" />
                <span>
                  {t('integrations.live_ping_fail')}: {livePing.reason || livePing.error || `status ${livePing.status}`}
                </span>
              </div>
            )}

            <Separator />

            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-2">
                {t('integrations.actions')}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refreshM.mutate()}
                  disabled={refreshM.isPending}
                  data-testid="qbo-refresh-tokens"
                >
                  {refreshM.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCcw className="h-4 w-4 mr-2" />
                  )}
                  {t('integrations.refresh_tokens')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => bootstrapM.mutate()}
                  disabled={bootstrapM.isPending}
                  data-testid="qbo-bootstrap-accounts"
                  className="col-span-2"
                >
                  {bootstrapM.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Calculator className="h-4 w-4 mr-2" />
                  )}
                  {t('integrations.qbo_setup_chart')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm(t('integrations.qbo_disconnect_confirm'))) {
                      disconnectM.mutate();
                    }
                  }}
                  disabled={disconnectM.isPending}
                  data-testid="qbo-disconnect"
                  className="text-rose-600 hover:text-rose-700 dark:text-rose-400"
                >
                  {disconnectM.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Unplug className="h-4 w-4 mr-2" />
                  )}
                  {t('integrations.qbo_disconnect')}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
